import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  listCommunityCookbooks,
  listCookbookCards,
  toggleLike,
  importCommunityCookbook,
  listLikedCookbooks,
  listLikedCommunityCookbooks,
  normalizeCommunityLanguageKey,
} from '../../services/communityCookbookService';
import {
  messageForCookbookQuotaCheckUnavailable,
} from '../../services/subscriptionQuotaService';
import { cookbookDeckRemovedByMonthlyLimitMessage } from '../../utils/cookbookDeckQuotaTriggerWatch';
import IconRenderer from '../ui/IconRenderer';
import { ReactComponent as CommunitySnippetIcon } from '../../Icons/snippet-icon.svg';
import PublishCookbookModal from './PublishCookbookModal';
import GitHubImportModal from './GitHubImportModal';
import SnippetViewer from './SnippetViewer';
import './CommunityPage.css';

function cardToSnippetViewer(card, cookbookLanguage) {
  return {
    id: card.id,
    title: card.title ?? card.prompt ?? 'Untitled',
    code: card.solution || card.code || '',
    notes: card.description || '',
    language: card.language || cookbookLanguage || 'Other',
  };
}

const PROGRAMMING_LANGUAGES = [
  'JavaScript',
  'Python',
  'Java',
  'C++',
  'C#',
  'Ruby',
  'PHP',
  'Swift',
  'Go',
  'Rust',
  'TypeScript',
  'SQL',
  'HTML/CSS',
  'Shell',
  'Other',
];

const SORT_OPTIONS = [
  { value: 'popular', label: 'Popular' },
  { value: 'recent', label: 'Recent' },
  { value: 'imports', label: 'Most imported' },
];

function SkeletonRows({ count = 6 }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="community-page__cb-row community-page__cb-row--skeleton" aria-hidden />
      ))}
    </>
  );
}

function LangFilterLabel({ lang, children }) {
  const iconName = lang === 'all' ? 'code' : lang;
  return (
    <>
      <IconRenderer
        name={iconName}
        type="language"
        size="small"
        className="community-page__filter-lang-icon"
        aria-hidden
      />
      <span>{children}</span>
    </>
  );
}

function LangFilter({ value, onChange, open, onToggle, onClose }) {
  const label = value === 'all' ? 'All langs' : value;
  return (
    <div className="community-page__filter-dropdown">
      <button
        type="button"
        className="community-page__filter-pill"
        onClick={onToggle}
        aria-expanded={open}
      >
        {value === 'all' ? (
          label
        ) : (
          <LangFilterLabel lang={value}>{label}</LangFilterLabel>
        )}
        <i className="ti ti-chevron-down" aria-hidden="true" />
      </button>
      {open && (
        <div className="community-page__filter-options">
          <button type="button" className="community-page__filter-option" onClick={() => { onChange('all'); onClose(); }}>
            <LangFilterLabel lang="all">All langs</LangFilterLabel>
          </button>
          {PROGRAMMING_LANGUAGES.map((lang) => (
            <button
              key={lang}
              type="button"
              className="community-page__filter-option"
              onClick={() => { onChange(lang); onClose(); }}
            >
              <LangFilterLabel lang={lang}>{lang}</LangFilterLabel>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SortFilter({ value, onChange, open, onToggle, onClose }) {
  const label = SORT_OPTIONS.find((o) => o.value === value)?.label || 'Popular';
  return (
    <div className="community-page__filter-dropdown">
      <button
        type="button"
        className="community-page__filter-pill"
        onClick={onToggle}
        aria-expanded={open}
      >
        {label}
        <i className="ti ti-chevron-down" aria-hidden="true" />
      </button>
      {open && (
        <div className="community-page__filter-options">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className="community-page__filter-option"
              onClick={() => { onChange(opt.value); onClose(); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CookbookLangIcon({ language, detail = false }) {
  const iconSize = detail ? 28 : 28;
  return (
    <IconRenderer
      name={language || 'code'}
      type="language"
      size={detail ? 'medium' : 'small'}
      className={`community-page__lang-badge${detail ? ' community-page__lang-badge--detail' : ''}`}
      style={{ width: iconSize, height: iconSize }}
      aria-hidden
    />
  );
}

function CookbookRow({ cookbook, selected, onClick }) {
  return (
    <div
      className={`community-page__cb-row${selected ? ' community-page__cb-row--selected' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <CookbookLangIcon language={cookbook.language} />
      <div className="community-page__cb-info">
        <span className="community-page__cb-title">{cookbook.name}</span>
        <span className="community-page__cb-stats">
          <CommunitySnippetIcon className="community-page__snippet-icon" aria-hidden />
          {cookbook.cardCount || 0}
          <span className="community-page__meta-sep" aria-hidden>·</span>
          <i className="ti ti-heart" aria-hidden="true" />
          {cookbook.likeCount || 0}
        </span>
      </div>
    </div>
  );
}

function RightPaneEmpty() {
  return (
    <div className="community-page__empty-state">
      <i className="ti ti-git-fork" aria-hidden="true" />
      <span>Select a cookbook to preview</span>
    </div>
  );
}

function RightPane({
  cookbook,
  cards,
  cardsLoading,
  isLiked,
  isActionDisabled,
  importing,
  onLike,
  onImport,
  onSnippetClick,
  snippetListRef,
}) {
  if (!cookbook) {
    return <RightPaneEmpty />;
  }

  return (
    <>
      <div className="community-page__detail-header">
        <div className="community-page__detail-top">
          <div className="community-page__detail-title-row">
            <CookbookLangIcon language={cookbook.language} detail />
            <div className="community-page__detail-title-block">
              <span className="community-page__detail-title">{cookbook.name}</span>
              <div className="community-page__detail-meta">
                <span>by {cookbook.authorDisplayName || 'anonymous'}</span>
                <span className="community-page__meta-sep" aria-hidden>·</span>
                <span className="community-page__detail-meta-stat">
                  <i className="ti ti-download" aria-hidden="true" />
                  {cookbook.importCount || 0}
                </span>
                <span className="community-page__meta-sep" aria-hidden>·</span>
                <span className="community-page__detail-meta-stat">
                  <CommunitySnippetIcon className="community-page__snippet-icon" aria-hidden />
                  {cookbook.cardCount || 0}
                </span>
                <span className="community-page__meta-sep" aria-hidden>·</span>
                <span className="community-page__detail-meta-stat">
                  <i className="ti ti-heart community-page__detail-meta-heart" aria-hidden="true" />
                  {cookbook.likeCount || 0}
                </span>
              </div>
            </div>
          </div>
          <div className="community-page__detail-actions">
            <button
              type="button"
              className={`community-page__btn-like${isLiked ? ' community-page__btn-like--liked' : ''}`}
              onClick={onLike}
              disabled={isActionDisabled}
              aria-label={isLiked ? 'Unlike' : 'Like'}
            >
              <i className={`ti ${isLiked ? 'ti-heart-filled' : 'ti-heart'}`} aria-hidden="true" />
              {isLiked ? 'Liked' : 'Like'}
            </button>
            <button
              type="button"
              className="community-page__btn-import"
              onClick={onImport}
              disabled={isActionDisabled || importing}
              aria-label="Import into your library"
            >
              <i className={`ti ti-${importing ? 'loader' : 'download'}`} aria-hidden="true" />
              {importing ? 'Importing…' : 'Import'}
            </button>
          </div>
        </div>

        {(cookbook.tags || []).length > 0 && (
          <div className="community-page__detail-tags">
            {cookbook.tags.map((tag) => (
              <span key={tag} className="community-page__tag">{String(tag).toLowerCase()}</span>
            ))}
          </div>
        )}

        {cookbook.description && (
          <p className="community-page__description">{cookbook.description}</p>
        )}
      </div>

      <div className="community-page__snippet-count">
        {cookbook.cardCount || cards.length} snippets
      </div>

      <div className="community-page__snippet-list" ref={snippetListRef}>
        {cardsLoading ? (
          <p className="community-page__snippet-loading">Loading snippets…</p>
        ) : cards.length === 0 ? (
          <p className="community-page__snippet-loading">No snippets in this cookbook.</p>
        ) : (
          cards.map((card) => (
            <div
              key={card.id}
              className="community-page__snippet-row"
              onClick={() => onSnippetClick(cardToSnippetViewer(card, cookbook.language))}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSnippetClick(cardToSnippetViewer(card, cookbook.language));
                }
              }}
            >
              <i className="ti ti-code" aria-hidden="true" />
              <span>{card.title ?? card.prompt ?? 'Untitled'}</span>
              <i className="ti ti-chevron-right" aria-hidden="true" />
            </div>
          ))
        )}
      </div>
    </>
  );
}

const CommunityPage = ({ userId, isAnonymousGuest, onNavigateToLibrary }) => {
  const [cookbooks, setCookbooks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [likedIds, setLikedIds] = useState(new Set());
  const [filterLang, setFilterLang] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [filterLikedOnly, setFilterLikedOnly] = useState(false);
  const [likedFeedCookbooks, setLikedFeedCookbooks] = useState([]);
  const [likedFeedLoading, setLikedFeedLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [gitHubImportOpen, setGitHubImportOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [importing, setImporting] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [indexBuilding, setIndexBuilding] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [openFilter, setOpenFilter] = useState(null);
  const [selectedSnippet, setSelectedSnippet] = useState(null);
  const snippetListRef = useRef(null);
  const filtersRef = useRef(null);

  const isActionDisabled = isAnonymousGuest || !userId;

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const selectedCookbook = useMemo(
    () => cookbooks.find((c) => c.id === selectedId) ?? null,
    [cookbooks, selectedId]
  );

  const displayCookbooks = filterLikedOnly ? likedFeedCookbooks : cookbooks;

  const visibleCookbooks = useMemo(() => {
    let items = displayCookbooks;
    if (filterLang !== 'all') {
      const langKey = normalizeCommunityLanguageKey(filterLang);
      items = items.filter((c) => normalizeCommunityLanguageKey(c.language) === langKey);
    }
    const term = searchQuery.trim().toLowerCase();
    if (!term) return items;
    return items.filter(
      (c) =>
        c.name?.toLowerCase().includes(term) ||
        c.tags?.some((t) => String(t).toLowerCase().includes(term)) ||
        c.authorDisplayName?.toLowerCase().includes(term)
    );
  }, [displayCookbooks, filterLang, searchQuery]);

  const listLoading = filterLikedOnly ? likedFeedLoading : loading;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setOpenFilter(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const refreshLikedIds = useCallback(async () => {
    if (!userId || isAnonymousGuest) {
      setLikedIds(new Set());
      return;
    }
    try {
      const items = await listLikedCookbooks(userId, 100);
      setLikedIds(new Set(items.map((item) => item.cookbookId || item.id)));
    } catch (err) {
      console.error('[CommunityPage] liked ids load failed', err);
    }
  }, [userId, isAnonymousGuest]);

  useEffect(() => {
    refreshLikedIds();
  }, [refreshLikedIds]);

  useEffect(() => {
    if (!filterLikedOnly || !userId || isAnonymousGuest) {
      setLikedFeedCookbooks([]);
      setLikedFeedLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLikedFeedLoading(true);
    listLikedCommunityCookbooks(userId, 100)
      .then((items) => {
        if (cancelled) return;
        setLikedFeedCookbooks(items);
        setSelectedId((prev) => {
          if (prev && items.some((c) => c.id === prev)) return prev;
          return items[0]?.id ?? null;
        });
      })
      .catch((err) => {
        console.error('[CommunityPage] liked feed load failed', err);
        if (!cancelled) {
          setLikedFeedCookbooks([]);
          setError('Failed to load liked cookbooks.');
        }
      })
      .finally(() => {
        if (!cancelled) setLikedFeedLoading(false);
      });

    return () => { cancelled = true; };
  }, [filterLikedOnly, userId, isAnonymousGuest]);

  const fetchCookbooks = useCallback(async ({ append = false } = {}) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError('');

    try {
      const result = await listCommunityCookbooks({
        language: filterLang,
        sortBy,
        startAfter: append ? lastDoc : null,
      });

      setIndexBuilding(result.indexBuilding);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);

      if (append) {
        setCookbooks((prev) => {
          const seen = new Set(prev.map((c) => c.id));
          const merged = [...prev];
          result.items.forEach((item) => {
            if (!seen.has(item.id)) merged.push(item);
          });
          return merged;
        });
      } else {
        setCookbooks(result.items);
        if (result.items.length > 0) {
          setSelectedId((prev) => {
            if (prev && result.items.some((c) => c.id === prev)) return prev;
            return result.items[0].id;
          });
        } else {
          setSelectedId(null);
        }
      }
    } catch (err) {
      console.error('[CommunityPage] fetch failed', err);
      setError('Failed to load community cookbooks.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filterLang, sortBy, lastDoc]);

  useEffect(() => {
    if (filterLikedOnly) return;
    setLastDoc(null);
    fetchCookbooks({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterLang, sortBy, filterLikedOnly]);

  useEffect(() => {
    if (!selectedId) {
      setCards([]);
      setSelectedSnippet(null);
      return;
    }

    setSelectedSnippet(null);
    let cancelled = false;
    setCardsLoading(true);
    listCookbookCards(selectedId)
      .then((loaded) => {
        if (!cancelled) setCards(loaded);
      })
      .catch((err) => {
        console.error('[CommunityPage] cards load failed', err);
        if (!cancelled) setCards([]);
      })
      .finally(() => {
        if (!cancelled) setCardsLoading(false);
      });

    snippetListRef.current?.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [selectedId]);

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    fetchCookbooks({ append: true });
  };

  const handleLike = async () => {
    if (!selectedCookbook || isActionDisabled) return;
    const wasLiked = likedIds.has(selectedCookbook.id);
    const prevCount = selectedCookbook.likeCount || 0;

    setLikedIds((prev) => {
      const next = new Set(prev);
      if (wasLiked) next.delete(selectedCookbook.id);
      else next.add(selectedCookbook.id);
      return next;
    });
    setCookbooks((prev) =>
      prev.map((c) =>
        c.id === selectedCookbook.id
          ? { ...c, likeCount: wasLiked ? Math.max(0, prevCount - 1) : prevCount + 1 }
          : c
      )
    );
    if (filterLikedOnly) {
      setLikedFeedCookbooks((prev) =>
        wasLiked
          ? prev.filter((c) => c.id !== selectedCookbook.id)
          : prev.some((c) => c.id === selectedCookbook.id)
            ? prev
            : [...prev, selectedCookbook]
      );
    }

    try {
      const result = await toggleLike(selectedCookbook.id, userId, selectedCookbook);
      setLikedIds((prev) => {
        const next = new Set(prev);
        if (result.liked) next.add(selectedCookbook.id);
        else next.delete(selectedCookbook.id);
        return next;
      });
      setCookbooks((prev) =>
        prev.map((c) =>
          c.id === selectedCookbook.id ? { ...c, likeCount: result.likeCount } : c
        )
      );
      if (filterLikedOnly) {
        setLikedFeedCookbooks((prev) =>
          result.liked
            ? prev.some((c) => c.id === selectedCookbook.id)
              ? prev.map((c) =>
                c.id === selectedCookbook.id ? { ...c, likeCount: result.likeCount } : c
              )
              : [...prev, { ...selectedCookbook, likeCount: result.likeCount }]
            : prev.filter((c) => c.id !== selectedCookbook.id)
        );
      }
    } catch (err) {
      setLikedIds((prev) => {
        const next = new Set(prev);
        if (wasLiked) next.add(selectedCookbook.id);
        else next.delete(selectedCookbook.id);
        return next;
      });
      setCookbooks((prev) =>
        prev.map((c) =>
          c.id === selectedCookbook.id ? { ...c, likeCount: prevCount } : c
        )
      );
      if (filterLikedOnly && wasLiked) {
        setLikedFeedCookbooks((prev) =>
          prev.some((c) => c.id === selectedCookbook.id)
            ? prev
            : [...prev, selectedCookbook]
        );
      }
      showToast('Could not update like. Try again.', 'error');
    }
  };

  const handleImport = async () => {
    if (!selectedCookbook || isActionDisabled || importing) return;
    setImporting(true);
    try {
      const result = await importCommunityCookbook(selectedCookbook.id, userId);
      if (!result.ok) {
        if (result.reason === 'quota_unavailable') {
          showToast(messageForCookbookQuotaCheckUnavailable(result.quota), 'error');
        } else if (result.reason === 'quota_exceeded') {
          showToast('Cookbook limit reached. Upgrade to import more.', 'error');
        } else if (result.reason === 'removed_by_quota_trigger') {
          showToast(cookbookDeckRemovedByMonthlyLimitMessage, 'error');
        } else {
          showToast('Import failed. Try again.', 'error');
        }
        return;
      }
      setCookbooks((prev) =>
        prev.map((c) =>
          c.id === selectedCookbook.id
            ? { ...c, importCount: (c.importCount || 0) + 1 }
            : c
        )
      );
      showToast('Imported! Open it in your library.', 'success');
    } catch (err) {
      showToast('Import failed. Try again.', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="community-page__container">
      {toast && (
        <div className={`community-page__toast community-page__toast--${toast.type}`}>
          {toast.message}
          {toast.type === 'success' && onNavigateToLibrary && (
            <button type="button" className="community-page__toast-link" onClick={onNavigateToLibrary}>
              Open in library →
            </button>
          )}
        </div>
      )}

      <header className="community-page__header">
        <div className="community-page__header-title">
          <i className="ti ti-git-fork" aria-hidden="true" />
          <span>Hello, World!</span>
        </div>
        <div className="community-page__header-actions">
          <button
            type="button"
            className="community-page__import-repo-btn"
            onClick={() => {
              if (isActionDisabled) {
                showToast('Create an account to import from GitHub.', 'info');
                return;
              }
              setGitHubImportOpen(true);
            }}
          >
            <i className="ti ti-brand-github" aria-hidden="true" />
            Import git repo
          </button>
          <button
            type="button"
            className="community-page__publish-btn"
            onClick={() => {
              if (isActionDisabled) {
                showToast('Create an account to publish to the community.', 'info');
                return;
              }
              setPublishOpen(true);
            }}
          >
            <i className="ti ti-plus" aria-hidden="true" />
            Publish
          </button>
        </div>
      </header>

      {indexBuilding && (
        <div className="community-page__index-notice" role="status">
          Indexes are still building. Using client-side sort for now.
        </div>
      )}

      <div className="community-page__split">
        <div className="community-page__left-pane">
          <div className="community-page__left-top" ref={filtersRef}>
            <div className="community-page__search-wrap">
              <i className="ti ti-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search cookbooks"
              />
            </div>
            <div className="community-page__filter-row">
              <LangFilter
                value={filterLang}
                onChange={setFilterLang}
                open={openFilter === 'lang'}
                onToggle={() => setOpenFilter((f) => (f === 'lang' ? null : 'lang'))}
                onClose={() => setOpenFilter(null)}
              />
              <SortFilter
                value={sortBy}
                onChange={setSortBy}
                open={openFilter === 'sort'}
                onToggle={() => setOpenFilter((f) => (f === 'sort' ? null : 'sort'))}
                onClose={() => setOpenFilter(null)}
              />
              <button
                type="button"
                className={`community-page__filter-like-btn${filterLikedOnly ? ' community-page__filter-like-btn--active' : ''}`}
                onClick={() => {
                  if (isActionDisabled) {
                    showToast('Create an account to filter liked cookbooks.', 'info');
                    return;
                  }
                  setFilterLikedOnly((on) => !on);
                  setOpenFilter(null);
                }}
                aria-label={filterLikedOnly ? 'Show all cookbooks' : 'Show liked cookbooks only'}
                aria-pressed={filterLikedOnly}
                title={filterLikedOnly ? 'Show all cookbooks' : 'Liked cookbooks'}
              >
                <i className="ti ti-heart" aria-hidden="true" />
              </button>
            </div>
            <span className="community-page__count-label">
              {visibleCookbooks.length} cookbook{visibleCookbooks.length === 1 ? '' : 's'}
            </span>
          </div>

          <div className="community-page__cb-list">
            {listLoading ? (
              <SkeletonRows count={6} />
            ) : error ? (
              <p className="community-page__list-error">{error}</p>
            ) : visibleCookbooks.length === 0 ? (
              <p className="community-page__list-empty">
                {filterLikedOnly ? 'No liked cookbooks yet.' : 'No cookbooks found.'}
              </p>
            ) : (
              visibleCookbooks.map((cb) => (
                <CookbookRow
                  key={cb.id}
                  cookbook={cb}
                  selected={cb.id === selectedId}
                  onClick={() => setSelectedId(cb.id)}
                />
              ))
            )}
            {hasMore && !loading && !filterLikedOnly && (
              <button
                type="button"
                className="community-page__load-more"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        </div>

        <div className="community-page__right-pane">
          {selectedSnippet && selectedCookbook ? (
            <SnippetViewer
              snippet={selectedSnippet}
              cookbook={selectedCookbook}
              isLiked={likedIds.has(selectedId)}
              importing={importing}
              isActionDisabled={isActionDisabled}
              onBack={() => setSelectedSnippet(null)}
              onLike={handleLike}
              onImport={handleImport}
            />
          ) : (
            <RightPane
              cookbook={selectedCookbook}
              cards={cards}
              cardsLoading={cardsLoading}
              isLiked={likedIds.has(selectedId)}
              isActionDisabled={isActionDisabled}
              importing={importing}
              onLike={handleLike}
              onImport={handleImport}
              onSnippetClick={setSelectedSnippet}
              snippetListRef={snippetListRef}
            />
          )}
        </div>
      </div>

      <PublishCookbookModal
        userId={userId}
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        onPublished={(newCookbook) => {
          setCookbooks((prev) => [{ ...newCookbook, id: newCookbook.id }, ...prev]);
          setSelectedId(newCookbook.id);
          setPublishOpen(false);
          showToast('Cookbook published!', 'success');
        }}
      />

      <GitHubImportModal
        userId={userId}
        open={gitHubImportOpen}
        onClose={() => setGitHubImportOpen(false)}
        onImported={(result) => {
          const count = result.cardCount ? `${result.cardCount} cards` : 'cookbook';
          showToast(`Imported ${count} into your library.`, 'success');
        }}
      />
    </div>
  );
};

export default CommunityPage;
